import React from 'react';
import { AIModel, formatCreditPrice } from '../../api/structs/model';
import Select from './Select';
import ModelPickerSheet from './ModelPickerSheet';

/** A provider Select's option — just the provider key + its display label. */
export interface PickerProviderOption {
  value: string;
  label: string;
}

/** A ModelPickerSheet option, scoped to one already-chosen provider. */
export interface PickerModelOption {
  value: string;
  label: string;
  sublabel?: string;
  rightLabel?: string;
}

// Providers with a fixed, meaningful position (Vertex AI first — it's easy to
// miss since it used to trail hundreds of OpenRouter entries with no marker).
// Anything else falls back to an alphabetized, prettified group.
const PROVIDER_ORDER = ['vertexai', 'openrouter'];
const PROVIDER_LABELS: Record<string, string> = {
  vertexai: 'Vertex AI',
  openrouter: 'OpenRouter',
  anthropic: 'Anthropic',
};

const prettifyProvider = (provider: string): string =>
  provider
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const providerLabel = (provider: string): string =>
  PROVIDER_LABELS[provider] || (provider ? prettifyProvider(provider) : 'Current');

/**
 * Providers that actually have at least one model in the fetched catalog —
 * Vertex AI first, any other provider alphabetized after — plus the given
 * selected provider even when the catalog holds nothing under it, so a
 * delisted pairing's provider control is never left without a home.
 */
export const providerOptionsFor = (
  models: AIModel[],
  selectedProvider: string
): PickerProviderOption[] => {
  const present = new Set(models.map((model) => model.provider));
  if (selectedProvider) present.add(selectedProvider);

  const ordered = PROVIDER_ORDER.filter((provider) => present.has(provider));
  const rest = Array.from(present)
    .filter((provider) => provider && !PROVIDER_ORDER.includes(provider))
    .sort((a, b) => providerLabel(a).localeCompare(providerLabel(b)));

  return [...ordered, ...rest].map((value) => ({ value, label: providerLabel(value) }));
};

/**
 * The model dropdown's options, narrowed to one already-chosen provider —
 * always including the currently-selected model even when the live catalog
 * omits it (registry hiccup, or a model the user set before it was delisted)
 * so a panel never silently swaps the model out from under a saved choice.
 */
export const modelOptionsFor = (
  models: AIModel[],
  selectedProvider: string,
  selectedModel: string
): PickerModelOption[] => {
  const options: PickerModelOption[] = models
    .filter((model) => model.provider === selectedProvider)
    .map((model) => ({
      value: model.id,
      label: model.name || model.id,
      sublabel: model.id,
      rightLabel: formatCreditPrice(model),
    }));
  if (selectedModel && !options.some((option) => option.value === selectedModel)) {
    options.unshift({ value: selectedModel, label: selectedModel, sublabel: selectedModel });
  }
  return options;
};

/** The first catalog model under a provider, or '' when it has none. */
export const firstModelFor = (models: AIModel[], provider: string): string =>
  models.find((model) => model.provider === provider)?.id ?? '';

export interface ProviderModelPickerProps {
  providerOptions: PickerProviderOption[];
  provider: string;
  onProviderChange: (provider: string) => void;
  providerAriaLabel: string;
  modelOptions: PickerModelOption[];
  model: string;
  onModelChange: (model: string) => void;
  modelAriaLabel: string;
}

/**
 * Provider Select + model ModelPickerSheet, the same two-control shape as
 * ChatProviderSelector (AI Editor / Prompt Enhancer). The reviewer tiers
 * can't reuse that component directly: it fetches its own chat-model list,
 * while check/confirm/pair/clip-QA share ONE already-fetched, vision-filtered
 * catalog across several pickers.
 *
 * Lifted out of `ReviewerSettingsSection` when the reviewer capability form
 * grew its own model tiers — one picker, one place, three surfaces.
 */
const ProviderModelPicker: React.FC<ProviderModelPickerProps> = ({
  providerOptions,
  provider,
  onProviderChange,
  providerAriaLabel,
  modelOptions,
  model,
  onModelChange,
  modelAriaLabel,
}) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
    <div className="w-full sm:w-[9rem] sm:flex-shrink-0">
      <Select
        options={providerOptions}
        value={provider}
        onChange={(event) => onProviderChange(event.target.value)}
        selectSize="sm"
        className="w-full min-w-0"
        aria-label={providerAriaLabel}
      />
    </div>
    <div className="w-full sm:flex-1">
      <ModelPickerSheet
        options={modelOptions}
        value={model}
        onChange={onModelChange}
        placeholder="Select model"
        searchable
        chipClassName="text-xs"
        ariaLabel={modelAriaLabel}
      />
    </div>
  </div>
);

export default ProviderModelPicker;
